import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import {
  useGetSEPsInInitiatePhaseQuery,
  useGetValidationTaskTemplatesQuery,
  useResetSEPsToValidationMutation,
} from './../../../../services/resetValidationPhaseSlice';
import SpinnerOverlay from '../../../../components/Spinner/Spinner';
import SEPSelectionTable from './SEPSelectionTable';
import TaskSelectionTable from './TaskSelectionTable';
import ResetActionButtons from './ResetActionButtons';
import ResetInstructions from './ResetInstructions';

interface ResultMessage {
  type: 'success' | 'error' | 'info';
  message: string;
}

const ValidationPhaseReset = () => {
  // API hooks
  const {
    data: sepsData,
    isLoading: sepsLoading,
    refetch: refetchSEPs,
  } = useGetSEPsInInitiatePhaseQuery();

  const { data: taskTemplatesData, isLoading: tasksLoading } =
    useGetValidationTaskTemplatesQuery();

  const [resetSEPsToValidation, { isLoading: isResetting }] =
    useResetSEPsToValidationMutation();

  // Local state
  const [selectedSEPs, setSelectedSEPs] = useState<number[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);
  const [sepSearch, setSepSearch] = useState('');
  const [taskSearch, setTaskSearch] = useState('');
  const [resultMessage, setResultMessage] = useState<ResultMessage | null>(
    null
  );

  // Filter SEPs based on search
  const filteredSEPs = useMemo(() => {
    if (!sepsData) return [];
    if (!sepSearch) return sepsData;
    const searchLower = sepSearch.toLowerCase();
    return sepsData.filter(
      (sep) =>
        sep.id.toString().includes(searchLower) ||
        sep.name?.toLowerCase().includes(searchLower) ||
        sep.creatorName?.toLowerCase().includes(searchLower) ||
        sep.creatorEmail?.toLowerCase().includes(searchLower)
    );
  }, [sepsData, sepSearch]);

  // Filter task templates based on search
  const filteredTasks = useMemo(() => {
    if (!taskTemplatesData) return [];
    if (!taskSearch) return taskTemplatesData;
    const searchLower = taskSearch.toLowerCase();
    return taskTemplatesData.filter(
      (task) =>
        task.id.toString().includes(searchLower) ||
        task.name?.toLowerCase().includes(searchLower) ||
        task.shortName?.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower)
    );
  }, [taskTemplatesData, taskSearch]);

  // Handle SEP selection
  const handleSEPSelect = useCallback((sepId: number) => {
    setSelectedSEPs((prev) =>
      prev.includes(sepId)
        ? prev.filter((id) => id !== sepId)
        : [...prev, sepId]
    );
  }, []);

  const handleSelectAllSEPs = useCallback(() => {
    setSelectedSEPs((prev) =>
      prev.length === filteredSEPs.length
        ? []
        : filteredSEPs.map((sep) => sep.id)
    );
  }, [filteredSEPs]);

  // Handle Task selection
  const handleTaskSelect = useCallback((taskId: number) => {
    setSelectedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  const handleSelectAllTasks = useCallback(() => {
    setSelectedTasks((prev) =>
      prev.length === filteredTasks.length
        ? []
        : filteredTasks.map((task) => task.id)
    );
  }, [filteredTasks]);

  // Handle clear selection
  const handleClearSelection = useCallback(() => {
    setSelectedSEPs([]);
    setSelectedTasks([]);
    setResultMessage(null);
  }, []);

  // Handle reset action
  const handleReset = async () => {
    if (selectedSEPs.length === 0) {
      setResultMessage({
        type: 'error',
        message: 'Please select at least one SEP.',
      });
      return;
    }
    if (selectedTasks.length === 0) {
      setResultMessage({
        type: 'error',
        message: 'Please select at least one validation task to attach.',
      });
      return;
    }

    try {
      const result = await resetSEPsToValidation({
        sepIDs: selectedSEPs,
        taskTemplateIDs: selectedTasks,
      }).unwrap();

      setResultMessage({
        type: 'success',
        message: `${result.message} Created ${result.createdTasksCount} new tasks. ${result.skippedDuplicates > 0 ? `Skipped ${result.skippedDuplicates} duplicate tasks.` : ''}`,
      });

      // Clear selections and refetch
      setSelectedSEPs([]);
      setSelectedTasks([]);
      refetchSEPs();
    } catch (error: any) {
      setResultMessage({
        type: 'error',
        message: error?.data || 'An error occurred while resetting SEPs.',
      });
    }
  };

  const isLoading = sepsLoading || tasksLoading;

  return (
    <Box sx={{ p: 3 }}>
      {(isLoading || isResetting) && <SpinnerOverlay />}

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2, px: 3 }}>
        Select SEPs in initiate phase and attach validation tasks to reset them
        back to validation phase.
      </Typography>

      {resultMessage && (
        <Alert
          severity={resultMessage.type}
          onClose={() => setResultMessage(null)}
          sx={{ mb: 2 }}
        >
          {resultMessage.message}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <SEPSelectionTable
          sepsData={sepsData}
          filteredSEPs={filteredSEPs}
          selectedSEPs={selectedSEPs}
          sepSearch={sepSearch}
          sepsLoading={sepsLoading}
          onSepSearchChange={setSepSearch}
          onSEPSelect={handleSEPSelect}
          onSelectAllSEPs={handleSelectAllSEPs}
          onRefresh={refetchSEPs}
        />

        <TaskSelectionTable
          taskTemplatesData={taskTemplatesData}
          filteredTasks={filteredTasks}
          selectedTasks={selectedTasks}
          taskSearch={taskSearch}
          tasksLoading={tasksLoading}
          onTaskSearchChange={setTaskSearch}
          onTaskSelect={handleTaskSelect}
          onSelectAllTasks={handleSelectAllTasks}
        />
      </Box>

      <ResetActionButtons
        selectedSEPsCount={selectedSEPs.length}
        selectedTasksCount={selectedTasks.length}
        isResetting={isResetting}
        onClearSelection={handleClearSelection}
        onReset={handleReset}
      />

      <ResetInstructions />
    </Box>
  );
};

export default ValidationPhaseReset;
